import { print } from 'graphql';
import { gql } from 'graphql-tag';
import type { RxCollection, RxJsonSchema } from 'rxdb';

import { getDateModified } from '../../utilities/utilityFunctions';
import { defaultDoc, getValueFromLocalStorage } from '../share/defaultDoc';

export interface DeficiencyActionedList {
  readonly componentFailureId: string;
  readonly isActive: boolean;
  readonly site: number;
  readonly discipline: number;
  readonly dateCreated: string;
  readonly dateModified: string;
  readonly assetId: number;
  readonly assetName: string;
  readonly capMapProjectId: string;
  readonly capMapTaskId: string;
  readonly workOrderId: string;
  readonly workRequestId: string;
  readonly workPackageId: number;
  readonly componentFailureStatus: number;
  readonly inspectionStatus: number;
  readonly componentRemovalCategory: string;
  readonly componentRemovalComments: string;
  readonly workOrderStatus: string;
  readonly workRequestStatus: string;
  readonly workPackageStatus: string;
  readonly fkComponentRemovalCategory: number;
  readonly pkDeficiencyUUID: string;
  readonly tMAppliedDate: string;
  readonly tMClosureDate: string;
}

export const deficiencyActionedListSchema: RxJsonSchema<DeficiencyActionedList> = {
  type: 'object',
  version: 0,
  description: 'describes active deficiency lists',
  primaryKey: 'pkDeficiencyUUID',
  properties: {
    pkDeficiencyUUID: { type: 'string', maxLength: 36 },
    assetId: { type: ['number', 'null'] },
    assetName: { type: ['string', 'null'] },
    isActive: { type: ['boolean', 'null'] },
    workPackageId: { type: ['number', 'null'] },
    workOrderId: { type: ['string', 'null'] },
    inspectionStatus: { type: ['number', 'null'] },
    componentFailureId: { type: ['string', 'null'] },
    workRequestId: { type: ['string', 'null'] },
    site: { type: ['number', 'null'] },
    discipline: { type: ['number', 'null'] },
    dateCreated: { type: ['string', 'null'] },
    dateModified: { type: ['string', 'null'] },
    capMapProjectId: { type: ['string', 'null'] },
    capMapTaskId: { type: ['string', 'null'] },
    fkComponentRemovalCategory: { type: ['number', 'null'] },
    componentRemovalCategory: { type: ['string', 'null'] },
    componentRemovalComments: { type: ['string', 'null'] },
    workOrderStatus: { type: ['string', 'null'] },
    workRequestStatus: { type: ['string', 'null'] },
    workPackageStatus: { type: ['string', 'null'] },
    componentFailureStatus: { type: ['number', 'null'] },
    tMAppliedDate: { type: ['string', 'null'] },
    tMClosureDate: { type: ['string', 'null'] },
  },
};

export type DeficiencyActionedListCollection = RxCollection<DeficiencyActionedList> | null;

export const deficiencyActionedListPullQueryBuilder = (document: DeficiencyActionedList) => {
  let doc = document;
  if (!doc) {
    (doc as unknown) = { ...defaultDoc };
  }

  const query = print(gql`
    query actionedDataForActiveDeficiencies(
      $siteId: Int!
      $disciplineId: Int!
      $lastRequested: DateTime
      $isLeadInspector: Boolean!
    ) {
      actionedDataForActiveDeficiencies(
        siteId: $siteId
        disciplineId: $disciplineId
        lastRequested: $lastRequested
        isLeadInspector: $isLeadInspector
      ) {
        assetId
        assetName
        isActive
        workPackageId
        workOrderId
        inspectionStatus
        componentFailureId
        workRequestId
        site
        discipline
        dateCreated
        dateModified
        capMapProjectId
        capMapTaskId
        fkComponentRemovalCategory
        componentRemovalCategory
        componentRemovalComments
        workPackageStatus
        workRequestStatus
        workOrderStatus
        pkDeficiencyUUID
        componentFailureStatus
        tMAppliedDate
        tMClosureDate
      }
    }
  `);

  const variables = {
    siteId: JSON.parse(getValueFromLocalStorage('selectedSite'))?.selectedSiteId,
    disciplineId: JSON.parse(getValueFromLocalStorage('selectedDiscipline'))?.selectedDisciplineId,
    lastRequested: getDateModified(
      doc?.dateModified,
      doc?.dateCreated,
      doc.site,
      doc.discipline,
      JSON.parse(getValueFromLocalStorage('selectedSite'))?.selectedSiteId,
      JSON.parse(getValueFromLocalStorage('selectedDiscipline'))?.selectedDisciplineId,
    ),
    isLeadInspector: getValueFromLocalStorage('isLeadInspector') === 'True',
  };

  return {
    query,
    variables,
  };
};

export default {};
